import React from "react";
import "./EbSkeleton.scss";

// ═════════════════════════════════════════════════════════════════════════════
// One shimmering chip. The whole skeleton vocabulary for the Execution
// Blueprint's live-review screens is this component plus each screen's own
// geometry classes.
//
// ⚠️ THIS FILE IS INTENTIONALLY IDENTICAL IN TWO REPOSITORIES.
//
//   10x-coach-frontend/src/components/CoachPanel/ExecutionBlueprint/
//     ExecutionBlueprintLiveReview/_shared/EbSkeleton.js
//   10x-frontend/src/components/UserPanel/ExecutionBlueprint/
//     ExecutionBlueprintLiveReview/_shared/EbSkeleton.js
//
// `diff` is the test; change one copy, change both in the same pass.
//
// It carries the stylesheet with it, which is the point: importing the
// component is the only thing a screen has to do to get the shared surface, so
// the paint and the markup cannot end up in different places. See
// `EbSkeleton.scss` for why there is now one shimmer instead of four.
//
// ── The house rule this serves ──────────────────────────────────────────────
// CLAUDE.md: "Skeletons mirror real layout, not a spinner or a Loading… line."
// This component is deliberately dumb — a sized rectangle and nothing else — so
// that mirroring stays the caller's job. Anything that knows what a Live Review
// row or a connector card looks like belongs in that screen, not here.
// ═════════════════════════════════════════════════════════════════════════════

/**
 * @param className  The caller's geometry class — width, height, radius,
 *                   margins. Composed onto `.eb-skel`, which supplies only the
 *                   colour and the sweep.
 * @param i          Stagger index. Rows in a list pass 0, 1, 2, … so the sweep
 *                   travels down them instead of every row pulsing at once.
 *                   Read by the stylesheet as the `--i` custom property.
 * @param tint       `true` on a tinted panel rather than on white — swaps the
 *                   resting colour only, since the highlight is transparent.
 * @param strong     `true` for a chip that stands in for a heading rather than
 *                   body text.
 * @param style      Escape hatch for a one-off size that does not deserve a
 *                   class of its own. Merged after `--i`, so a caller can
 *                   override the stagger if it ever needs to.
 */
const EbSkeleton = ({ className = "", i, tint, strong, style }) => (
  <span
    className={`eb-skel${tint ? " eb-skel--tint" : ""}${
      strong ? " eb-skel--strong" : ""
    }${className ? ` ${className}` : ""}`}
    // `aria-hidden` on every chip, not just on the outermost wrapper. Callers
    // are inconsistent about hiding their skeleton containers, and a lone
    // unlabelled <span> announced to a screen reader is noise with no content
    // behind it. The screens themselves own the `role="status"` announcement.
    aria-hidden="true"
    style={i ? { "--i": i, ...style } : style}
  />
);

export default EbSkeleton;
